import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  CircularProgress,
  Alert,
  IconButton,
  Tooltip
} from '@mui/material';
import {
  ArrowBack
} from '@mui/icons-material';
import { brandColors } from '../../../theme/theme';
import apiService from '../../../lib/services/apiService';
import { useFilmSubscription } from '../../../hooks/useFilmSubscription';
import BlurredContent from '../../../components/Subscription/BlurredContent';
import { FILM_TIERS } from '../../../lib/filmSubscriptionTiers';

// Deep Dive Components
import DeepDiveFilters from '../components/DeepDive/DeepDiveFilters';
import DeepDiveTabs from '../components/DeepDive/DeepDiveTabs';

// Deep Dive Utilities
import {
  transformEthnicityTrendData,
  transformAgeTrendData,
  calculateQuadrantDistribution
} from '../../../lib/utils/deepDiveUtils';

const FilmDeepDiveDetail = () => {
  const { ttId } = useParams();
  const navigate = useNavigate();
  const { canAccessDeepDive } = useFilmSubscription();
  
  // Deep Dive tab state
  const [deepDiveData, setDeepDiveData] = useState(null);
  const [deepDiveLoading, setDeepDiveLoading] = useState(false);
  const [deepDiveFilters, setDeepDiveFilters] = useState({
    movieFrequency: '',
    ageGroup: '',
    gender: '',
    ethnicity: '',
    income: '',
    identity: '',
    surveyDateFrom: '',
    surveyDateTo: ''
  });
  
  // Trends data state (for ALL rounds - ethnicity and age trends over time)
  const [trendsData, setTrendsData] = useState(null);
  const deepDiveRef = useRef(null);

  // Deep Dive data fetching
  const fetchDeepDiveData = async (customFilters = null) => {
    try {
      setDeepDiveLoading(true);
      
      const activeFilters = customFilters || deepDiveFilters;
      
      // Build API filters for aggregation - MOST RECENT ROUND ONLY
      const apiFilters = {
        aggregate: true,
        group_by: 'ethnicity,agegroup,gender,movie_frequency_category',
        imdb_id: ttId,
        film_recency_order: 1  // Only most recent round
      };
      
      // Add optional filters
      if (activeFilters.movieFrequency) apiFilters.movie_frequency_category = activeFilters.movieFrequency;
      if (activeFilters.ageGroup) apiFilters.agegroup = activeFilters.ageGroup;
      if (activeFilters.gender) apiFilters.gender = activeFilters.gender;
      if (activeFilters.ethnicity) apiFilters.ethnicity = activeFilters.ethnicity;
      if (activeFilters.surveyDateFrom) apiFilters.survey_date_from = activeFilters.surveyDateFrom;
      if (activeFilters.surveyDateTo) apiFilters.survey_date_to = activeFilters.surveyDateTo;
      
      // Fetch aggregated data
      const response = await apiService.getFilmSurveyToplinesAggregated(apiFilters);
      
      console.log('🔍 Deep Dive: API Response received', {
        success: response?.success,
        hasData: !!response?.data,
        dataLength: response?.data?.length,
        fullResponse: response
      });
      
      if (response?.success && response.data?.length > 0) {
        console.log('📊 Deep Dive: Processing aggregated data', {
          recordCount: response.data.length,
          sampleRecord: response.data[0],
          allFieldsInFirstRecord: Object.keys(response.data[0])
        });

        // Calculate heat map data (by ethnicity)
        const ethnicityData = response.data.reduce((acc, record) => {
          const ethnicity = record.ethnicity || 'Unknown';
          if (!acc[ethnicity]) {
            acc[ethnicity] = {
              ethnicity,
              totalResponses: 0,
              totalAware: 0,
              totalInterest: 0,
              totalTheater: 0,
              totalPayment: 0,
              totalHeat: 0,
              records: []
            };
          }
          // API returns capitalized field names: TotalResponses, TotalAware, etc.
          acc[ethnicity].totalResponses += record.TotalResponses || 0;
          acc[ethnicity].totalAware += (record.TotalAware || 0);
          acc[ethnicity].totalInterest += (record.TotalInterest || 0);
          acc[ethnicity].totalTheater += (record.TotalTheater || 0);
          acc[ethnicity].totalPayment += (record.TotalWillingToPay || 0);
          acc[ethnicity].totalHeat += (record.TotalHeat || 0);
          acc[ethnicity].records.push(record);
          return acc;
        }, {});

        // Calculate percentages for heat map
        const heatMapData = Object.values(ethnicityData).map(eth => {
          return {
            ethnicity: eth.ethnicity,
            awareness: eth.totalResponses > 0 ? Math.round((eth.totalAware / eth.totalResponses) * 100) : 0,
            interest: eth.totalResponses > 0 ? Math.round((eth.totalInterest / eth.totalResponses) * 100) : 0,
            theater: eth.totalResponses > 0 ? Math.round((eth.totalTheater / eth.totalResponses) * 100) : 0,
            payment: eth.totalResponses > 0 ? Math.round((eth.totalPayment / eth.totalResponses) * 100) : 0,
            heat: eth.totalResponses > 0 ? Math.round((eth.totalHeat / eth.totalResponses) * 100) : 0,
            responses: eth.totalResponses
          };
        }).filter(eth => eth.responses > 0); // Only include ethnicities with data

        console.log('📊 Deep Dive: Heat map data calculated', heatMapData);

        // Transform trend data
        const trendData = transformEthnicityTrendData(response.data);
        console.log('📈 Deep Dive: Trend data calculated', trendData);
        
        // Calculate quadrant data
        const quadrantData = calculateQuadrantDistribution(response.data, 'aware-interested');
        console.log('🎯 Deep Dive: Quadrant data calculated', quadrantData);
        
        setDeepDiveData({
          rawData: response.data,
          heatMapData,
          trendData
        });
      } else {
        console.warn('⚠️ Deep Dive: No data received or empty response', {
          responseSuccess: response?.success,
          hasData: !!response?.data,
          dataLength: response?.data?.length,
          response: response
        });
        setDeepDiveData(null);
      }

    } catch (error) {
      console.error('❌ Deep Dive: Error fetching data:', error);
      setDeepDiveData(null);
      
      // Show user-friendly error message based on error type
      if (error.type === 'rateLimit' || error.status === 429) {
        console.warn('⚠️ Rate limit hit - Deep Dive data unavailable');
      } else if (error.type === 'timeout' || error.message?.includes('too long')) {
        console.warn('⚠️ Request timeout - Deep Dive data unavailable');
      }
    } finally {
      setDeepDiveLoading(false);
    }
  };

  // Fetch trends data for ALL rounds (for ethnicity and age trend charts)
  const fetchDeepDiveTrendsData = async () => {
    try {
      console.log('📈 Deep Dive Trends: Fetching ALL rounds for trend charts');
      
      // Build API filters - NO film_recency_order to get ALL rounds
      const apiFilters = {
        aggregate: true,
        group_by: 'ethnicity,agegroup,FilmRecencyOrder,SurveyCompleteDate,releasedate',
        imdb_id: ttId
      };
      
      // Fetch aggregated data for all rounds
      const response = await apiService.getFilmSurveyToplinesAggregated(apiFilters);
      
      console.log('📊 Deep Dive Trends: Response received', {
        success: response?.success,
        dataLength: response?.data?.length
      });
      
      if (response?.success && response.data?.length > 0) {
        // Group data by round to count rounds
        const roundsSet = new Set(response.data.map(d => d.FilmRecencyOrder));
        const roundCount = roundsSet.size;
        
        console.log('✅ Deep Dive Trends: Data processed', {
          totalRecords: response.data.length,
          roundsDetected: roundCount,
          rounds: Array.from(roundsSet).sort((a, b) => a - b)
        });
        
        // Transform ethnicity trend data
        const ethnicityTrends = transformEthnicityTrendData(response.data);
        
        // Transform age trend data
        const ageTrends = transformAgeTrendData(response.data);
        
        setTrendsData({
          rawData: response.data,
          ethnicityTrends,
          ageTrends,
          roundCount
        });
      } else {
        console.warn('⚠️ Deep Dive Trends: No data received');
        setTrendsData(null);
      }
    } catch (error) {
      console.error('❌ Deep Dive Trends: Error fetching data:', error);
      setTrendsData(null);
      
      // Show user-friendly error message based on error type
      if (error.type === 'rateLimit' || error.status === 429) {
        console.warn('⚠️ Rate limit hit - Trends data unavailable');
      } else if (error.type === 'timeout' || error.message?.includes('too long')) {
        console.warn('⚠️ Request timeout - Trends data unavailable');
      }
    }
  };

  // Handle Deep Dive filter changes
  const handleDeepDiveFilterChange = (key, value) => {
    const newFilters = { ...deepDiveFilters, [key]: value };
    setDeepDiveFilters(newFilters);
    fetchDeepDiveData(newFilters);
  };

  const handleDeepDiveReset = () => {
    const emptyFilters = {
      movieFrequency: '',
      ageGroup: '',
      gender: '',
      ethnicity: '',
      income: '',
      identity: '',
      surveyDateFrom: '',
      surveyDateTo: ''
    };
    setDeepDiveFilters(emptyFilters);
    fetchDeepDiveData(emptyFilters);
  };

  const handleDeepDivePresetApply = (presetFilters) => {
    const newFilters = { ...deepDiveFilters, ...presetFilters };
    setDeepDiveFilters(newFilters);
    fetchDeepDiveData(newFilters);
  };

  // Load initial deep dive data
  useEffect(() => {
    if (ttId) {
      fetchDeepDiveData();
      fetchDeepDiveTrendsData(); // Also fetch ALL rounds for trend charts
    }
  }, [ttId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (deepDiveRef.current) {
      deepDiveRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, []);

  const content = (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      {/* Header with Back Button */}
      <Box sx={{ mb: 4, display: 'flex', alignItems: 'center', gap: 2 }}>
        <Tooltip title="Back to Deep Dive List">
          <IconButton
            onClick={() => navigate('/film-tracking/deep-dive')}
            sx={{
              backgroundColor: brandColors.primary + '10',
              '&:hover': {
                backgroundColor: brandColors.primary + '20'
              }
            }}
          >
            <ArrowBack />
          </IconButton>
        </Tooltip>
        <Box>
          <Typography variant="h3" sx={{ fontWeight: 700, mb: 1 }}>
            Film Deep Dive Analytics
          </Typography>
          <Typography variant="h6" color="text.secondary">
            Advanced demographic and behavioral insights for {ttId}
          </Typography>
        </Box>
      </Box>

      {/* Deep Dive Content */}
      <Box ref={deepDiveRef}>
        {/* Advanced Filters */}
        <DeepDiveFilters
          filters={deepDiveFilters}
          onFilterChange={handleDeepDiveFilterChange}
          onReset={handleDeepDiveReset}
          onPresetApply={handleDeepDivePresetApply}
        />

        {deepDiveLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <CircularProgress size={60} />
          </Box>
        ) : deepDiveData ? (
          <DeepDiveTabs 
            data={deepDiveData}
            trendsData={trendsData}
            imdbId={ttId}
          />
        ) : (
          <Alert severity="info" sx={{ mb: 3 }}>
            No deep dive data available for this film. Try adjusting your filters.
          </Alert>
        )}
      </Box>
    </Container>
  );

  // Only show blurred content if user doesn't have access (super_admins have access via forceUnlock)
  if (!canAccessDeepDive) {
    return (
      <BlurredContent
        requiredTier={FILM_TIERS.ENTERPRISE}
        featureName="Film Deep Dive Analytics"
        blurAmount={12}
      >
        {content}
      </BlurredContent>
    );
  }

  return content;
};

export default FilmDeepDiveDetail;
